import React, { useEffect, useState } from "react";
import moment from "moment";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  LuCalendar,
  LuClock,
  LuIndianRupee,
  LuScale,
  LuWallet,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import Button from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import Input from "../ui/Input";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  dialogInputClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldLabelClass,
  filterGridClass,
  formFieldIconClass,
  summaryCardTitleClass,
  summaryCardValueClass,
} from "../ui/styles";
import {
  getEditSalary,
  setEditSalary,
  toggleCreateModal,
} from "../../features/salaries/salariesSlice";
import { IFormInput } from "../../types/salaries";

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type Props = {
  handleSaveSalaries: (data: IFormInput) => void;
};

const EditForm = ({ handleSaveSalaries }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editSalary = useAppSelector(getEditSalary);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subAmount, setSubAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalWithdrawal, setTotalWithdrawal] = useState<number>(0);
  const [lastTotalWithdrawal, setLastTotalWithdrawal] = useState<number>(0);
  const [lastTotalAmount, setLastTotalAmount] = useState<number>(0);

  const { handleSubmit, register, reset, getValues } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (id) {
      setIsLoading(true);
      await handleSaveSalaries({
        ...data,
        status: 0,
        id: editSalary?.id,
      });
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
    dispatch(setEditSalary(null));
  };

  const recalculate = () => {
    const { roundoff_amount, withdrawal_deposit } = getValues();

    const total =
      parseFloat(`${subAmount || 0}`) + parseFloat(`${roundoff_amount || 0}`);
    setTotalAmount(total);

    setLastTotalWithdrawal(
      parseFloat(`${totalWithdrawal || 0}`) -
        parseFloat(`${withdrawal_deposit || 0}`)
    );

    setLastTotalAmount(
      parseFloat(`${total || 0}`) - parseFloat(`${withdrawal_deposit || 0}`)
    );

    return true;
  };

  useEffect(() => {
    if (editSalary && editSalary.id) {
      setSubAmount(editSalary.amount || 0);
      setTotalAmount(
        (editSalary.amount || 0) + (editSalary.roundoff_amount || 0)
      );
      setTotalWithdrawal(editSalary.withdrawal_amount || 0);
      setLastTotalWithdrawal(
        (editSalary.withdrawal_amount || 0) -
          (editSalary.withdrawal_deposit || 0)
      );
      setLastTotalAmount(editSalary.total_amount || 0);

      reset({
        fromDate: editSalary.from_date,
        toDate: editSalary.to_date,
        roundoff_amount: editSalary.roundoff_amount,
        withdrawal_deposit: editSalary.withdrawal_deposit,
      });
    }
  }, [editSalary]);

  return (
    <Dialog open onClose={handleCloseModal}>
      <DialogContent size="wide" className={dialogMobileContentClass}>
        <DialogHeader className={dialogMobileHeaderClass}>
          <DialogTitle className={dialogTitleClass}>
            હિસાબ ની માહિતી
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className={filterGridClass}>
              <div className="space-y-2">
                <label className={fieldLabelClass}>તારીખ થી</label>

                <div className="relative">
                  <LuCalendar className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={moment(editSalary?.from_date).format("DD-MM-YYYY")}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>તારીખ સુધી</label>

                <div className="relative">
                  <LuCalendar className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={moment(editSalary?.to_date).format("DD-MM-YYYY")}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>હિસાબ કલાકો × રેટ</label>

                <div className="relative">
                  <LuClock className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={`${editSalary?.note?.hours || 0} × ${
                      editSalary?.note?.amount || 0
                    }`}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>હિસાબ અમાઉન્ટ</label>

                <div className="relative">
                  <LuScale className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={subAmount || 0}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>રાઉન્ડ ઑફ</label>

                <div className="relative">
                  <LuIndianRupee className={formFieldIconClass} />
                  <Input
                    type="number"
                    step="any"
                    placeholder="રાઉન્ડ ઑફ દાખલ કરો"
                    className={dialogInputClass()}
                    {...register("roundoff_amount", { required: false })}
                    onKeyUp={recalculate}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>હિસાબ ટોટલ અમાઉન્ટ</label>

                <div className="relative">
                  <LuScale className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={`${(totalAmount || 0).toFixed(2)}`}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>ટોટલ ઉપાડ</label>

                <div className="relative">
                  <LuWallet className={formFieldIconClass} />
                  <Input
                    type="text"
                    readOnly
                    value={`${(totalWithdrawal || 0).toFixed(2)}`}
                    className={cx(dialogInputClass(), "bg-gray-100")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className={fieldLabelClass}>કપાત ઉપાડ</label>

                <div className="relative">
                  <LuIndianRupee className={formFieldIconClass} />
                  <Input
                    type="number"
                    step="any"
                    placeholder="કપાત ઉપાડ દાખલ કરો"
                    className={dialogInputClass()}
                    {...register("withdrawal_deposit", { required: false })}
                    onKeyUp={recalculate}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={cx(cardClass, "p-4")}>
                <div className={summaryCardTitleClass}>બાકી ઉપાડ અમાઉન્ટ</div>
                <div className={cx(summaryCardValueClass, "mt-2")}>
                  {formatAmount(lastTotalWithdrawal)}
                </div>
              </div>

              <div className={cx(cardClass, "p-4")}>
                <div className={summaryCardTitleClass}>પગાર અમાઉન્ટ</div>
                <div className={cx(summaryCardValueClass, "mt-2")}>
                  {formatAmount(lastTotalAmount)}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseModal}
              disabled={isLoading}
            >
              બંધ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading && <span className={buttonSpinnerClass} />}
              આગળ વધો
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditForm;
